import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimespanPredefinedType } from '@thingslog/repositories';
import { format, isBefore, isValid, parse } from 'date-fns';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Control,
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  Path,
  useWatch,
} from 'react-hook-form';

export type TimespanPickerMode = 'none' | 'predefined' | 'custom';

interface TimespanPickerModeOption {
  value: TimespanPickerMode;
  label: string;
}

interface Props<TFormValues extends FieldValues> {
  control: Control<TFormValues>;
  mode: TimespanPickerMode;
  modeName?: Path<TFormValues>;
  onModeChange: (mode: TimespanPickerMode) => void;
  predefinedName: Path<TFormValues>;
  fromDateName: Path<TFormValues>;
  toDateName: Path<TFormValues>;
  disabled?: boolean;
  title?: string;
  description?: string;
  modeOptions?: TimespanPickerModeOption[];
  predefinedLabel?: string;
  tourTargetBase?: string;
  className?: string;
  children?: ReactNode;
}

export const predefinedTimespanOptions: Array<{
  value: TimespanPredefinedType;
  label: string;
  labelKey: string;
}> = [
  { value: 'today', label: 'Today', labelKey: 'dashboard_timespan_today' },
  { value: 'yesterday', label: 'Yesterday', labelKey: 'dashboard_timespan_yesterday' },
  { value: 'this-week', label: 'This Week', labelKey: 'dashboard_timespan_this_week' },
  { value: 'this-month', label: 'This Month', labelKey: 'dashboard_timespan_this_month' },
  { value: 'this-year', label: 'This Year', labelKey: 'dashboard_timespan_this_year' },
  { value: 'past-1d', label: 'Past 1 Day', labelKey: 'dashboard_timespan_past_1d' },
  { value: 'past-1w', label: 'Past 1 Week', labelKey: 'dashboard_timespan_past_1w' },
  { value: 'past-1m', label: 'Past 1 Month', labelKey: 'dashboard_timespan_past_1m' },
  { value: 'past-1y', label: 'Past 1 Year', labelKey: 'dashboard_timespan_past_1y' },
];

const defaultModeOptions: TimespanPickerModeOption[] = [
  { value: 'predefined', label: 'Predefined' },
  { value: 'custom', label: 'Custom' },
];

const modeLabelKeys: Record<TimespanPickerMode, string> = {
  none: 'dashboard_period_dashboard',
  predefined: 'dashboard_period_predefined',
  custom: 'dashboard_period_custom',
};

const storageDateFormat = 'yyyy-MM-dd';
const displayDateFormat = 'dd/MM/yyyy';

const parseStorageDate = (value: unknown): Date | null => {
  if (typeof value !== 'string' || !value) {
    return null;
  }

  const date = parse(value, storageDateFormat, new Date());

  return isValid(date) ? date : null;
};

export const TimespanPickerFields = <TFormValues extends FieldValues>({
  control,
  mode,
  modeName,
  onModeChange,
  predefinedName,
  fromDateName,
  toDateName,
  disabled = false,
  title,
  description,
  modeOptions = defaultModeOptions,
  predefinedLabel,
  tourTargetBase,
  className = 'flex min-w-0 flex-col gap-3 border-0 border-t border-solid border-gray-200 pt-4',
  children,
}: Props<TFormValues>): JSX.Element => {
  const { t } = useTranslation();
  const resolvedPredefinedLabel =
    predefinedLabel ?? t('dashboard_predefined_period', 'Predefined Period');
  const customFromDateValue = useWatch({
    control,
    name: fromDateName,
  });
  const customFromDate = parseStorageDate(customFromDateValue);

  const renderModePicker = (
    selectedMode: TimespanPickerMode,
    handleModeChange: (nextMode: TimespanPickerMode) => void
  ): JSX.Element => (
    <FormControl component="fieldset">
      <RadioGroup
        value={selectedMode}
        row
        onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
          handleModeChange(event.target.value as TimespanPickerMode);
        }}
      >
        {modeOptions.map((option: TimespanPickerModeOption) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio size="small" disabled={disabled} />}
            label={t(modeLabelKeys[option.value], option.label)}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );

  const renderDateField = (
    name: Path<TFormValues>,
    label: string,
    requiredMessage: string,
    minDate?: Date | null
  ): JSX.Element => (
    <Controller
      name={name}
      control={control}
      rules={{
        required: requiredMessage,
        validate: (value: unknown): true | string => {
          const date = parseStorageDate(value);

          if (!date || !minDate) {
            return true;
          }

          return isBefore(date, minDate)
            ? t('dashboard_end_date_before_start_error', 'End date cannot be before start date')
            : true;
        },
      }}
      render={({
        field,
        fieldState,
      }: {
        field: ControllerRenderProps<TFormValues, Path<TFormValues>>;
        fieldState: ControllerFieldState;
      }): JSX.Element => (
        <DatePicker
          label={label}
          inputFormat={displayDateFormat}
          value={parseStorageDate(field.value)}
          minDate={minDate ?? undefined}
          onChange={(date: Date | null): void => {
            field.onChange(date ? format(date, storageDateFormat) : '');
          }}
          disabled={disabled}
          renderInput={(params: TextFieldProps): JSX.Element => (
            <TextField
              {...params}
              size="small"
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              disabled={disabled}
            />
          )}
        />
      )}
    />
  );

  return (
    <section className={className}>
      {(title || description) && (
        <div className="flex flex-col gap-1">
          {title && <Typography variant="subtitle2">{title}</Typography>}
          {description && (
            <Typography variant="caption" className="text-gray-600">
              {description}
            </Typography>
          )}
        </div>
      )}

      {children}

      <div data-dashboard-tour={tourTargetBase ? `${tourTargetBase}-mode` : undefined}>
        {modeName ? (
          <Controller
            name={modeName}
            control={control}
            render={({
              field,
            }: {
              field: ControllerRenderProps<TFormValues, Path<TFormValues>>;
            }): JSX.Element =>
              renderModePicker(
                (field.value as TimespanPickerMode) ?? mode,
                (nextMode: TimespanPickerMode): void => {
                  field.onChange(nextMode);
                  onModeChange(nextMode);
                }
              )
            }
          />
        ) : (
          renderModePicker(mode, onModeChange)
        )}
      </div>

      {mode === 'predefined' && (
        <div data-dashboard-tour={tourTargetBase ? `${tourTargetBase}-preset` : undefined}>
          <Controller
            name={predefinedName}
            control={control}
            rules={{
              required: t(
                'dashboard_predefined_period_required',
                'Please select a predefined period'
              ),
            }}
            render={({
              field,
              fieldState,
            }: {
              field: ControllerRenderProps<TFormValues, Path<TFormValues>>;
              fieldState: ControllerFieldState;
            }): JSX.Element => (
              <FormControl fullWidth size="small" error={!!fieldState.error}>
                <InputLabel>{resolvedPredefinedLabel}</InputLabel>
                <Select {...field} label={resolvedPredefinedLabel} disabled={disabled}>
                  {predefinedTimespanOptions.map(
                    (option: {
                      value: TimespanPredefinedType;
                      label: string;
                      labelKey: string;
                    }) => (
                      <MenuItem key={option.value} value={option.value}>
                        {t(option.labelKey, option.label)}
                      </MenuItem>
                    )
                  )}
                </Select>
                {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </div>
      )}

      {mode === 'custom' && (
        <div
          className="grid grid-cols-1 gap-3 md:grid-cols-2"
          data-dashboard-tour={tourTargetBase ? `${tourTargetBase}-custom` : undefined}
        >
          {renderDateField(
            fromDateName,
            t('start_date', 'Start Date'),
            t('dashboard_start_date_required', 'Start date is required')
          )}
          {renderDateField(
            toDateName,
            t('end_date', 'End Date'),
            t('dashboard_end_date_required', 'End date is required'),
            customFromDate
          )}
        </div>
      )}
    </section>
  );
};
